import type { ObjectValues } from 'utils/types.ts';
import { denormalizeShop, normalizeShop, type RemoteShopData, type ShopData } from 'service/shop/model.ts';
import type { ResponseOfferCategoryData, OfferCategoryData } from 'service/category/model.ts';
import { normalizeOfferCategoryData, denormalizeOfferCategoryData } from 'service/category/model.ts';

export const UnpackedOfferCondition = {
	OPEN_BOX: 'openBox',
	USED: 'used',
	REFURBISHED: 'refurbished',
} as const;
export type Condition = ObjectValues<typeof UnpackedOfferCondition>;

export type RemoteUnpackedOfferData = {
	category: null | ResponseOfferCategoryData;
	condition: Condition;
	condition_description: string;
	description: string;
	id: string;
	images: string[];
	impression: string;
	impression_control: string;
	link: string;
	name: string;
	price: number;
	seo_name: string;
	shop: RemoteShopData;
	warranty: number;
};

export type UnpackedOfferData = {
	category: null | OfferCategoryData;
	condition: Condition;
	conditionDescription: string;
	description: string;
	id: string;
	images: string[];
	impression: string;
	impressionControl: string;
	link: string;
	name: string;
	price: number;
	seoName: string;
	shop: ShopData;
	warranty: number;
	loadedOnPageNumber?: number;
};

// Abridged version of the type returned when searching for list of unpacked offers
export type RemoteSearchUnpackedOffer = Pick<
	RemoteUnpackedOfferData,
	'condition' | 'id' | 'images' | 'impression' | 'impression_control' | 'name' | 'price' | 'seo_name'
> & { shop: { id: number; name: string; seo_name: string } };

export type SearchUnpackedOffer = Pick<
	UnpackedOfferData,
	'condition' | 'id' | 'images' | 'impression' | 'impressionControl' | 'name' | 'price' | 'seoName'
> & { shop: { id: number; name: string; seoName: string } };

export function normalizeUnpackedOffer(offer: RemoteUnpackedOfferData): UnpackedOfferData {
	return {
		category: offer.category && normalizeOfferCategoryData(offer.category),
		condition: offer.condition,
		conditionDescription: offer.condition_description,
		description: offer.description,
		id: offer.id,
		images: offer.images,
		impression: offer.impression,
		impressionControl: offer.impression_control,
		link: offer.link,
		name: offer.name,
		price: offer.price,
		seoName: offer.seo_name,
		shop: normalizeShop(offer.shop),
		warranty: offer.warranty,
	};
}

export function denormalizeUnpackedOffer(offer: UnpackedOfferData): RemoteUnpackedOfferData {
	return {
		category: offer.category && denormalizeOfferCategoryData(offer.category),
		condition: offer.condition,
		condition_description: offer.conditionDescription,
		description: offer.description,
		id: offer.id,
		images: offer.images,
		impression: offer.impression,
		impression_control: offer.impressionControl,
		link: offer.link,
		name: offer.name,
		price: offer.price,
		seo_name: offer.seoName,
		shop: denormalizeShop(offer.shop),
		warranty: offer.warranty,
	};
}

export function normalizeSearchSearchUnpackedOffer(offer: RemoteSearchUnpackedOffer): SearchUnpackedOffer {
	return {
		condition: offer.condition,
		id: offer.id,
		images: offer.images,
		impression: offer.impression,
		impressionControl: offer.impression_control,
		name: offer.name,
		price: offer.price,
		seoName: offer.seo_name,
		shop: { id: offer.shop.id, name: offer.shop.name, seoName: offer.shop.seo_name },
	};
}

export function denormalizeSearchSearchUnpackedOffer(offer: SearchUnpackedOffer): RemoteSearchUnpackedOffer {
	return {
		condition: offer.condition,
		id: offer.id,
		images: offer.images,
		impression: offer.impression,
		impression_control: offer.impressionControl,
		name: offer.name,
		price: offer.price,
		seo_name: offer.seoName,
		shop: { id: offer.shop.id, name: offer.shop.name, seo_name: offer.shop.seoName },
	};
}
